import React, { useContext, useEffect, useRef, useState } from 'react'
import SilageStepLayout from '../SilageStepLayout'
import { Checkbox, Form } from 'semantic-ui-react'
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { HarvestingWrapper, StyledDivider, StyledGroup } from './style';
import { FormFields } from '~/components/FormFields';
import InfoPopup from '~/components/InfoPopup';
import { ClimaOptionsContext } from '.';
import { GRASS_SILAGE_ID } from '../..';
import HarvestSlider from './HarvestSlider';
import LocationTableTooltip from '~/pages/Farmer/ClimateCheck/AnswerClimateCheck/Steps/ForageProdStep/GrassSilage/LocationTableTooltip';
import { handleErrors } from '~/utils/helpers';
import CO2Service from '~/api/services/co2';
import { useParams } from 'react-router-dom';
import LinkPopup from '../LinkPopup';
import ErrorIcon from '~/pages/Farmer/ClimateCheck/AnswerClimateCheck/Steps/ErrorIcon';

const FirstGrassStep: React.FC = () => {
  const { id } = useParams()
  const { control, setValue, clearErrors } = useFormContext()

  const [isLoadingFreshMassYield, setIsloadingFreshMassYield] = useState(false)
  const isFirstRender = useRef(true)

  const {
    locationFavorabilityTypes,
    fertilizationTypes,
    pruningPhases,
    fieldSizes,
    silageTypes,
    cuttingFrequencies,
    setIsLoadingResults,
    setWorkOperations
  } = useContext(ClimaOptionsContext)

  const watchHarvesting = useWatch({ control, name: 'grassSilage.harvestingMethods' }) || []
  const watchPruningnPhaseId = useWatch({ control, name: 'grassSilage.pruningPhaseId' })
  const watchAvgFieldSize = useWatch({ control, name: 'grassSilage.averageFieldSize' })
  const watchFertilisation = useWatch({ control, name: 'grassSilage.fertilizationType' })
  const watchLocation = useWatch({ control, name: 'grassSilage.locationFavorability' })
  const watchCuttingFreq = useWatch({ control, name: 'grassSilage.cuttingFrequency' })
  const watchMainHarvesting = useWatch({ control, name: 'grassSilage.harvestingMethods[0]' })

  const harvestingOptions = silageTypes?.find(silageType => silageType.id === GRASS_SILAGE_ID)?.harvestingMethods || []

  async function getPredefinedData() {
    setIsLoadingResults(true)
    setIsloadingFreshMassYield(true)
    try {
      const payload = {
        harvestingMethodId: watchHarvesting[0]?.id,
        pruningPhaseId: watchPruningnPhaseId,
        locationFavorability: watchLocation,
        fertilizationType: watchFertilisation,
        cuttingFrequency: watchCuttingFreq
      }
      const response = await CO2Service.getGrassSilagePredefinedData(id, payload)
      setWorkOperations(response.data.workOperations)
      setValue('grassSilage.freshMassYield', response.data.freshMassYield)
      setValue('grassSilage.energy', response.data.energy)
      setValue('grassSilage.rawProtein', response.data.rawProtein)
      setValue('grassSilage.dryMatter', response.data.dryMatter)
    } catch (error) {
      handleErrors(error)
    } finally {
      setIsloadingFreshMassYield(false)
      setIsLoadingResults(false)
    }

  }

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }

    if (watchMainHarvesting?.id &&
      watchPruningnPhaseId &&
      watchLocation &&
      watchFertilisation &&
      watchCuttingFreq) {
      getPredefinedData()
    }
  }, [
    watchMainHarvesting?.id,
    watchPruningnPhaseId,
    watchLocation,
    watchFertilisation,
    watchCuttingFreq
  ])

  function handleChecked(harvestingOption) {
    return [...watchHarvesting, { ...harvestingOption, percent: watchHarvesting.length === 0 ? '100' : 0 }]
  }

  function handleUnchecked(harvestingOption, indexChecked) {
    //deselection of secundary harvest
    if (watchHarvesting?.length > 0 && indexChecked > 0) {
      const tempWatchHarvest = [...watchHarvesting]
      tempWatchHarvest[0].percent = 100
      return tempWatchHarvest.filter(harvest => harvest.id !== harvestingOption.id)
    }

    //deselection of primary harvest
    return []
  }

  return <SilageStepLayout
    infoText={`Die Abfrage zur Grassilagebewirtschaftung bezieht sich auf eine durchschnittliche Grünlandfläche Ihres Betriebs. Bitte berücksichtigen Sie dies bei der Eingabe Ihrer Daten. 
      Falls sich Bewirtschaftung oder Erträge auf mehreren Flächen unterscheiden, bilden Sie geeignete Mittelwerte, 
      um eine repräsentative Angabe zu ermöglichen. Die Ernte- und Ertragsparameter werden benötigt, um den Ertrag schätzen zu können.`}
  >
    <StyledGroup widths={'equal'}>
      <Controller
        control={control}
        name='grassSilage.harvestingMethods'
        rules={{
          required: true
        }}
        render={({ fieldState: { error } }) => (
          <HarvestingWrapper error={Boolean(error)}>
            <label className='title' style={{ width: 'fit-content' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div>{'Ernteverfahren'}</div>
                <InfoPopup
                  content={'Das Ernteverfahren wirkt sich auf die Arbeitsgänge aus. '}
                  styleIcon={{ cursor: 'pointer', fontSize: '1.3em' }}
                  position={'top center'}
                />
              </div>
              <StyledDivider />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {harvestingOptions.map(harvestingOption => {

                const indexChecked = watchHarvesting.findIndex(harvesting => harvesting.id === harvestingOption.id)
                const harvestChecked = indexChecked >= 0
                const singleHarvestSelected = watchHarvesting.length === 1
                const maxHarvestSelected = watchHarvesting.length === 2
                const displayProcent = maxHarvestSelected && harvestChecked
                const displaySliderLabels = maxHarvestSelected && harvestChecked && watchHarvesting[1].id === harvestingOption.id

                return <Form.Field key={harvestingOption.id}>
                  <Checkbox
                    key={harvestingOption.id}
                    style={{ marginBottom: 3 }}
                    label={displayProcent ? `${harvestingOption.label} (${watchHarvesting[indexChecked]?.percent}%)` : harvestingOption.label}
                    className={singleHarvestSelected && !harvestChecked ? 'disable-harvest-method' : ''}
                    disabled={maxHarvestSelected && !harvestChecked}
                    checked={harvestChecked}
                    onChange={(_, data) => {
                      const harvestingOptions = data.checked ?
                        handleChecked(harvestingOption) :
                        handleUnchecked(harvestingOption, indexChecked)

                      setValue('grassSilage.harvestingMethods', harvestingOptions)
                      if (watchHarvesting?.length > 0) {
                        clearErrors('grassSilage.harvestingMethods')
                      }
                    }}
                  />
                  {displaySliderLabels && <HarvestSlider
                    primaryHarvestKey='grassSilage.harvestingMethods[0].percent'
                    secondaryHarvestKey='grassSilage.harvestingMethods[1].percent'
                  />}
                </Form.Field>
              })}
            </div>
          </HarvestingWrapper>
        )}
      />
      <Controller
        control={control}
        name='grassSilage.pruningPhaseId'
        rules={{
          required: true
        }}
        render={({ fieldState: { error } }) => (
          <Form.Field error={Boolean(error)}>
            <label className='title' style={{ width: 'fit-content' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div>{'Schnittzeitpunkt'}</div>
                <InfoPopup
                  content={'Der Schnittzeitpunkt wirkt sich auf die Vorbelegung des Ertrags, Energie- und Rohproteingehaltes aus.'}
                  styleIcon={{ cursor: 'pointer', fontSize: '1.3em' }}
                  position={'top center'}
                />
              </div>
              <StyledDivider />
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              {pruningPhases?.map(prunningPhase => {
                return <Checkbox
                  radio
                  key={prunningPhase.id}
                  label={prunningPhase.label}
                  checked={watchPruningnPhaseId === prunningPhase.id}
                  onClick={() => {
                    setValue('grassSilage.pruningPhaseId', prunningPhase.id)
                    clearErrors('grassSilage.pruningPhaseId')
                  }}
                />
              })}
            </div>
          </Form.Field>
        )}
      />
    </StyledGroup>

    <StyledGroup widths={'equal'}>
      <FormFields.Input
        control={control}
        rules={{
          required: true
        }}
        withoutAsterisk
        noErrorMessage
        typeInput={'controled'}
        label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <label>{'Anbaufläche'}</label>
          <LinkPopup
            content='Sammelantrag oder Ackerschlagkartei'
          />
        </div>}
        suffix=' ha'
        placeholder={'in ha'}
        name={'grassSilage.areaUnderCultivation'}
      />
      <Controller
        control={control}
        name='grassSilage.averageFieldSize'
        rules={{
          required: true
        }}
        render={({ fieldState: { error } }) => (
          <Form.Field error={Boolean(error)}>
            <label className='title' style={{ width: 'fit-content' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div>{'Durchschnittliche Schlaggröße'}</div>
                <InfoPopup
                  content={'Geben Sie an, wie groß Ihre Flächen im Durchschnitt sind. Die Wahl der durchschnittlichen Schlaggröße wirkt sich auf die Kalkulation des Dieselbedarfs aus.'}
                  styleIcon={{ cursor: 'pointer', fontSize: '1.3em' }}
                  position={'top center'}
                />
                <LinkPopup
                  content='Ackerschlagkartei'
                />
              </div>
              <StyledDivider />
            </label>
            <div style={{ display: 'flex', gap: 10 }}>
              {fieldSizes.map(field => {
                return <Checkbox
                  radio
                  key={field.key}
                  label={field.label}
                  checked={watchAvgFieldSize === field.key}
                  onClick={() => {
                    setValue('grassSilage.averageFieldSize', field.key)
                    clearErrors('grassSilage.averageFieldSize')
                  }}
                />
              })}
            </div>
          </Form.Field>
        )}
      />
      <FormFields.Input
        control={control}
        rules={{
          required: true
        }}
        withoutAsterisk
        decimalScale={2}
        allowDecimals
        noErrorMessage
        typeInput={'controled'}
        label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <label>{'Durchschnittliche Entfernung von Hofstelle zu Grünland'}</label>
          <InfoPopup
            content={'Diese Angabe wirkt sich auf den Dieselverbrauch aus.'}
            styleIcon={{ cursor: 'pointer', fontSize: '1.3em' }}
            position={'top center'}
          />
          <LinkPopup
            content='Ackerschlagkartei'
          />
        </div>}
        suffix=' km'
        placeholder={'in km'}
        name={'grassSilage.averageDistance'}
      />
    </StyledGroup>

    <StyledGroup widths={'equal'}>
      <Form.Field>
        <FormFields.Select
          label={<label style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <div>{'Standort'}</div>
            <InfoPopup
              content={<LocationTableTooltip />}
              position={'top center'}
              hoverable={true}
              backgroundTransparent
              styleIcon={{ fontSize: '1.3em' }}
            />
          </label>}
          control={control}
          onChange={(e, data) => {
            setValue('grassSilage.locationFavorability', data.value)
            clearErrors('grassSilage.locationFavorability')
          }}
          rules={{
            required: true
          }}
          placeholder='Anzahl Standort'
          name='grassSilage.locationFavorability'
          options={locationFavorabilityTypes.map(type => ({ value: type.key, text: type.label }))}
          noErrorMessage
          withoutAsterisk
        />

        <FormFields.Select
          label={'Schnitthäufigkeit'}
          className={'marginField'}
          control={control}
          rules={{
            required: true
          }}
          scrolling
          upward
          placeholder='Anzahl Schnitthäufigkeit'
          name='grassSilage.cuttingFrequency'
          onChange={(e, data) => {
            setValue('grassSilage.cuttingFrequency', data.value)
            clearErrors('grassSilage.cuttingFrequency')
          }}
          options={cuttingFrequencies.map(type => ({ value: type.key, text: type.label }))}
          noErrorMessage
          withoutAsterisk
        />
      </Form.Field>

      <Form.Field>
        <Controller
          name='grassSilage.fertilizationType'
          rules={{
            required: true
          }}
          render={({ fieldState: { error } }) => (
            <Form.Field error={Boolean(error)} id={'grassSilage.fertilizationType'}>
              <div style={{ display: 'flex', gap: 20, marginTop: 32 }}>
                {fertilizationTypes.map(fertType => {
                  return <Checkbox
                    radio
                    key={fertType.key}
                    label={fertType.label}
                    checked={watchFertilisation === fertType.key}
                    onClick={() => {
                      setValue('grassSilage.fertilizationType', fertType.key)
                      clearErrors('grassSilage.fertilizationType')
                    }}
                  />
                })}
              </div>
            </Form.Field>
          )}
        />
        <FormFields.Input
          style={{ marginTop: 31 }}
          label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <label>{'Wie ist der Trockensubstanz-Gehalt der Grassilage?'}</label>
            <ErrorIcon
              control={control}
              name={'grassSilage.dryMatter'}
            />
          </div>}
          name='grassSilage.dryMatter'
          rules={{
            required: true,
            validate: (value) => {
              const transformValue = typeof (value) === 'string' ? parseFloat(value.replace(',', '.')) : value
              if (transformValue > 100) {
                return 'Der Trockensubstanz-Gehalt darf maximal 100 % TM betragen.'
              }
              return true
            }
          }}
          suffix=' % TM'
          placeholder='in % TM'
          allowDecimals
          decimalScale={2}
          noErrorMessage
          withoutAsterisk
          control={control}
          typeInput={'controled'}
        />
      </Form.Field>
    </StyledGroup>

    <StyledGroup widths={'equal'} style={{ alignItems: 'end' }}>

      <FormFields.Input
        control={control}
        rules={{
          required: true,
          validate: (value) => {
            const transformedValue = parseFloat(value.toString().replace(',', '.'))
            if (transformedValue > 999.99) {
              return 'Der Frischmasse-Ertrag der Grassilage darf maximal 999,99 dt FM/ha betragen.'
            }
            return true
          }
        }}
        withoutAsterisk
        noErrorMessage
        loading={isLoadingFreshMassYield}
        decimalScale={2}
        allowDecimals
        suffix=' dt FM/ha'
        typeInput={'controled'}
        label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <label>{'Wie hoch ist der Frischmasse-Ertrag der Grassilage pro Jahr nach Abzug der Ernteverluste?'}</label>
          <ErrorIcon
            control={control}
            name={'grassSilage.freshMassYield'}
          />
        </div>}
        placeholder={'in dt FM/ha'}
        name={'grassSilage.freshMassYield'}
      />

      <FormFields.Input
        control={control}
        rules={{
          required: true,
          validate: (value) => {
            const transformedValue = parseFloat(value.toString().replace(',', '.'))
            if (transformedValue > 99.99) {
              return 'Der Energiegehalt der Silage darf maximal 99,99 MJ NEL/kg TM betragen.'
            }
            return true
          }
        }}
        withoutAsterisk
        noErrorMessage
        decimalScale={2}
        allowDecimals
        suffix=' MJ NEL/kg TM'
        typeInput={'controled'}
        label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <label>{'Energiegehalt der Silage'}</label>
          <InfoPopup
            content={'Hier ist der durchschnittliche Energiegehalt aller Schnitte gemeint. Falls Analysen der verschiedenen Grassilagen vorhanden sind, bilden sie einen Mittelwert.'}
            styleIcon={{ cursor: 'pointer', fontSize: '1.3em' }}
            position={'top center'}
          />
          <LinkPopup
            content='Analyseergebnisse der Silagen'
          />
          <ErrorIcon
            control={control}
            name={'grassSilage.energy'}
          />
        </div>}
        placeholder={'in MJ NEL/kg TM'}
        name={'grassSilage.energy'}
      />

      <FormFields.Input
        control={control}
        rules={{
          required: true,
          validate: (value) => {
            const transformedValue = parseFloat(value.toString().replace(',', '.'))
            if (transformedValue > 999.99) {
              return 'Der Rohproteingehalt der Silage darf maximal 999,99 g XP/kg TM betragen.'
            }
            return true
          }
        }}
        withoutAsterisk
        decimalScale={2}
        allowDecimals
        noErrorMessage
        suffix=' g XP/kg TM'
        typeInput={'controled'}
        label={<div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          <label>{'Rohproteingehalt der Silage'}</label>
          <InfoPopup
            content={'Hier ist der durchschnittliche Rohproteingehalt aller Schnitte gemeint. Falls Analysen der verschiedenen Grassilagen vorhanden sind, bilden sie einen Mittelwert.'}
            styleIcon={{ cursor: 'pointer', fontSize: '1.3em' }}
            position={'top center'}
          />
          <LinkPopup
            content='Analyseergebnisse der Silagen'
          />
          <ErrorIcon
            control={control}
            name={'grassSilage.rawProtein'}
          />
        </div>}
        placeholder={'in g XP/kg TM'}
        name={'grassSilage.rawProtein'}
      />
    </StyledGroup>
  </SilageStepLayout>
}
export default FirstGrassStep